import { useMemo, useState } from 'react';
import { ArrowRight, Users, CalendarCheck, XCircle, BadgeCheck, Banknote, Megaphone, PiggyBank } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCampaignReport } from '@/hooks/useCampaignReport';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { cn } from '@/lib/utils';

interface CampaignFlowReportProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
}

/**
 * The advertising flow at a glance: for every campaign, how many leads came in,
 * how many were booked, how many of those bookings were cancelled, how many
 * turned into completed sales, and what those sales were worth.
 *
 * Cohort-based: leads created in the period are followed to whatever happened
 * to them, even when the job was completed after the period ended.
 */

interface FlowRow {
  id: string;
  name: string;
  source: string | null;
  isActive: boolean;
  leads: number;
  booked: number;
  cancelled: number;
  sales: number;
  open: number;
  turnover: number;
  /** Company profit after parts, tolls, card fees and tech commission. Before ad spend. */
  netProfit: number;
  spend: number;
}

const pct = (num: number, den: number): string => (den > 0 ? `${Math.round((num / den) * 100)}%` : '–');

function StageBar({ value, max, className }: { value: number; max: number; className: string }) {
  const width = max > 0 ? Math.max((value / max) * 100, value > 0 ? 3 : 0) : 0;
  return (
    <div className="mt-1.5 h-1 w-full rounded-full bg-muted">
      <div className={cn('h-1 rounded-full', className)} style={{ width: `${width}%` }} />
    </div>
  );
}

export const CampaignFlowReport = ({ startDate, endDate, timezone: propTimezone }: CampaignFlowReportProps) => {
  const { timezone: contextTimezone } = useTimezoneUtils();
  const timezone = propTimezone || contextTimezone;
  const { locale } = useCompanyLocale();
  const [showEmpty, setShowEmpty] = useState(false);

  const { data: campaigns, isLoading } = useCampaignReport(startDate, endDate, false, timezone);

  const money = useMemo(
    () => (n: number) =>
      new Intl.NumberFormat(locale.locale, {
        style: 'currency',
        currency: locale.currency,
        maximumFractionDigits: 0,
      }).format(n),
    [locale],
  );

  const rows = useMemo<FlowRow[]>(() => {
    if (!campaigns) return [];
    return campaigns
      .map((c) => ({
        id: c.campaign_id,
        name: c.campaign_name,
        source: c.lead_source_name,
        isActive: c.is_active,
        leads: c.total_leads,
        booked: c.total_bookings,
        cancelled: c.jobs_cancelled,
        sales: c.total_jobs,
        open: Math.max(c.total_bookings - c.jobs_cancelled - c.total_jobs, 0),
        turnover: c.total_revenue,
        netProfit: c.net_profit,
        spend: c.actual_cost,
      }))
      .filter((r) => showEmpty || r.leads > 0 || r.booked > 0 || r.turnover > 0 || r.spend > 0)
      .sort((a, b) => b.turnover - a.turnover || b.leads - a.leads);
  }, [campaigns, showEmpty]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (t, r) => ({
          leads: t.leads + r.leads,
          booked: t.booked + r.booked,
          cancelled: t.cancelled + r.cancelled,
          sales: t.sales + r.sales,
          open: t.open + r.open,
          turnover: t.turnover + r.turnover,
          netProfit: t.netProfit + r.netProfit,
          spend: t.spend + r.spend,
        }),
        { leads: 0, booked: 0, cancelled: 0, sales: 0, open: 0, turnover: 0, netProfit: 0, spend: 0 },
      ),
    [rows],
  );

  const max = useMemo(
    () => ({
      leads: Math.max(...rows.map((r) => r.leads), 0),
      booked: Math.max(...rows.map((r) => r.booked), 0),
      cancelled: Math.max(...rows.map((r) => r.cancelled), 0),
      sales: Math.max(...rows.map((r) => r.sales), 0),
      turnover: Math.max(...rows.map((r) => r.turnover), 0),
      netProfit: Math.max(...rows.map((r) => r.netProfit), 0),
    }),
    [rows],
  );

  const hasSpend = totals.spend > 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[104px] w-full rounded-lg" />
        <Skeleton className="h-[320px] w-full rounded-lg" />
      </div>
    );
  }

  const stages = [
    { key: 'leads', label: 'Leads', value: totals.leads, icon: Users, color: 'text-primary', sub: 'came in' },
    { key: 'booked', label: 'Booked', value: totals.booked, icon: CalendarCheck, color: 'text-info', sub: `${pct(totals.booked, totals.leads)} of leads` },
    { key: 'cancelled', label: 'Cancelled', value: totals.cancelled, icon: XCircle, color: 'text-destructive', sub: `${pct(totals.cancelled, totals.booked)} of booked` },
    { key: 'sales', label: 'Sales', value: totals.sales, icon: BadgeCheck, color: 'text-success', sub: `${pct(totals.sales, totals.booked)} of booked` },
  ] as const;

  return (
    <div className="space-y-4">
      {/* Overall flow strip */}
      <Card className="overflow-hidden">
        <div className="flex flex-col divide-y divide-border lg:flex-row lg:items-stretch lg:divide-x lg:divide-y-0">
          <div className="flex shrink-0 flex-col justify-center gap-1 bg-primary/10 px-5 py-4 lg:w-[168px]">
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-primary">Campaign flow</span>
            <span className="text-xs text-muted-foreground">
              {rows.length} campaign{rows.length === 1 ? '' : 's'} with activity
            </span>
          </div>

          {stages.map((s, i) => (
            <div key={s.key} className="relative flex min-w-0 flex-1 flex-col justify-center gap-1 px-5 py-4">
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <s.icon className={cn('h-[13px] w-[13px] shrink-0', s.color)} />
                {s.label}
              </span>
              <span className="text-2xl font-semibold tracking-tight tabular-nums">{s.value.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground">{s.sub}</span>
              {i < stages.length - 1 && (
                <ArrowRight className="absolute -right-2.5 top-1/2 hidden h-4 w-4 -translate-y-1/2 text-muted-foreground/60 lg:block" />
              )}
            </div>
          ))}

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 bg-success/10 px-5 py-4">
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Banknote className="h-[13px] w-[13px] shrink-0 text-success" />
              Turnover
            </span>
            <span className="text-2xl font-semibold tracking-tight tabular-nums">{money(totals.turnover)}</span>
            <span className="text-xs text-muted-foreground">
              {totals.sales > 0 ? `${money(totals.turnover / totals.sales)} per sale` : 'no completed sales yet'}
            </span>
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-5 py-4">
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <PiggyBank className={cn('h-[13px] w-[13px] shrink-0', totals.netProfit >= 0 ? 'text-success' : 'text-destructive')} />
              Net profit
            </span>
            <span className={cn('text-2xl font-semibold tracking-tight tabular-nums', totals.netProfit < 0 && 'text-destructive')}>
              {money(totals.netProfit)}
            </span>
            <span className="text-xs text-muted-foreground">
              {totals.turnover > 0 ? `${pct(totals.netProfit, totals.turnover)} margin · after parts & tech` : 'after parts & tech'}
            </span>
          </div>

          {hasSpend && (
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-5 py-4">
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Megaphone className="h-[13px] w-[13px] shrink-0 text-warning" />
                Ad spend
              </span>
              <span className="text-2xl font-semibold tracking-tight tabular-nums">{money(totals.spend)}</span>
              <span className="text-xs text-muted-foreground">
                {totals.spend > 0 ? `${(totals.turnover / totals.spend).toFixed(1)}× return` : ''}
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* Per-campaign table */}
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-2 border-b px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[15px] font-semibold">By campaign</p>
            <p className="text-xs text-muted-foreground">
              Leads created in the period, followed through to booking, cancellation and completed sale.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="flow-show-empty" checked={showEmpty} onCheckedChange={setShowEmpty} />
            <Label htmlFor="flow-show-empty" className="text-xs text-muted-foreground">Show campaigns with no activity</Label>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] text-[13px]">
            <thead>
              <tr className="border-b text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                <th className="px-5 py-2.5 text-left">Campaign</th>
                <th className="w-[13%] px-3 py-2.5 text-right">Leads</th>
                <th className="w-[13%] px-3 py-2.5 text-right">Booked</th>
                <th className="w-[13%] px-3 py-2.5 text-right">Cancelled</th>
                <th className="w-[13%] px-3 py-2.5 text-right">Sales</th>
                <th className="w-[14%] px-3 py-2.5 text-right">Turnover</th>
                <th className="w-[14%] px-3 py-2.5 text-right">Net profit</th>
                {hasSpend && <th className="w-[12%] px-5 py-2.5 text-right">Ad spend</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={hasSpend ? 8 : 7} className="px-5 py-10 text-center text-sm text-muted-foreground">
                    No campaign activity in this period.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-b-0 hover:bg-muted/50">
                  <td className="px-5 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{r.name}</span>
                      {!r.isActive && (
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">Paused</span>
                      )}
                    </div>
                    {r.source && <p className="text-xs text-muted-foreground">{r.source}</p>}
                  </td>

                  <td className="px-3 py-3 align-top text-right tabular-nums">
                    {r.leads.toLocaleString()}
                    <StageBar value={r.leads} max={max.leads} className="bg-primary/70" />
                  </td>

                  <td className="px-3 py-3 align-top text-right tabular-nums">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">{r.booked.toLocaleString()}</span>
                      </TooltipTrigger>
                      <TooltipContent>{pct(r.booked, r.leads)} of leads booked{r.open > 0 ? ` · ${r.open} still open` : ''}</TooltipContent>
                    </Tooltip>
                    <StageBar value={r.booked} max={max.booked} className="bg-info/70" />
                  </td>

                  <td className="px-3 py-3 align-top text-right tabular-nums">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={cn('cursor-default', r.cancelled > 0 && 'text-destructive')}>{r.cancelled.toLocaleString()}</span>
                      </TooltipTrigger>
                      <TooltipContent>{pct(r.cancelled, r.booked)} of bookings cancelled</TooltipContent>
                    </Tooltip>
                    <StageBar value={r.cancelled} max={max.cancelled} className="bg-destructive/60" />
                  </td>

                  <td className="px-3 py-3 align-top text-right tabular-nums">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">{r.sales.toLocaleString()}</span>
                      </TooltipTrigger>
                      <TooltipContent>{pct(r.sales, r.booked)} of bookings completed</TooltipContent>
                    </Tooltip>
                    <StageBar value={r.sales} max={max.sales} className="bg-success/70" />
                  </td>

                  <td className="px-3 py-3 align-top text-right font-semibold tabular-nums">
                    {money(r.turnover)}
                    <StageBar value={r.turnover} max={max.turnover} className="bg-success" />
                  </td>

                  <td className={cn('px-3 py-3 align-top text-right font-semibold tabular-nums', r.netProfit < 0 && 'text-destructive')}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">{money(r.netProfit)}</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {r.turnover > 0 ? `${pct(r.netProfit, r.turnover)} margin · ` : ''}after parts, tolls, card fees and tech commission
                      </TooltipContent>
                    </Tooltip>
                    <StageBar value={Math.max(r.netProfit, 0)} max={max.netProfit} className="bg-success/70" />
                  </td>

                  {hasSpend && (
                    <td className="px-5 py-3 align-top text-right tabular-nums text-muted-foreground">
                      {r.spend > 0 ? money(r.spend) : '–'}
                      {r.spend > 0 && (
                        <p className="text-[11px]">{(r.turnover / r.spend).toFixed(1)}× return</p>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-background font-semibold">
                  <td className="px-5 py-3">Total</td>
                  <td className="px-3 py-3 text-right tabular-nums">{totals.leads.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{totals.booked.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{totals.cancelled.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{totals.sales.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{money(totals.turnover)}</td>
                  <td className={cn('px-3 py-3 text-right tabular-nums', totals.netProfit < 0 && 'text-destructive')}>{money(totals.netProfit)}</td>
                  {hasSpend && <td className="px-5 py-3 text-right tabular-nums">{money(totals.spend)}</td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
};
